import { Browser } from '@capacitor/browser';
import { Capacitor } from '@capacitor/core';
import { useFeatureValue } from '@growthbook/growthbook-react';
import { Button, Typography } from '@mui/material';
import { useState } from 'react';
import { FlexibleStepperContextProps } from 'src/components/FlexibleStepper/types';
import { useFlexibleStepper } from 'src/components/FlexibleStepper/useFlexibleStepper';
import AuthStepperFooter from 'src/features/auth/presentation/components/AuthStepperFooter/AuthStepperFooter';
import AnimatedStepWrapper from 'src/features/auth/presentation/components/SignUpStepper/AnimatedStepWrapper';
import useAuthMutations from 'src/features/auth/presentation/hooks/useAuthMutations';
import useIdVerification from 'src/features/verification/presentation/hooks/useIdVerification';
import { FEATURE_FLAGS } from 'src/utils/growthBook/growthBook.constants';

interface DocumentVerificationStepProps {
  onSuccess?: (stepperContext: FlexibleStepperContextProps) => void;
  onFinishedVerification?: () => void;
}

export default function DocumentVerificationStep({
  onFinishedVerification,
}: DocumentVerificationStepProps) {
  const [isPending, setIsPending] = useState(false);
  const {
    data: idVerificationSession,
    isLoading,
    refetch,
  } = useIdVerification();
  const stepperContext = useFlexibleStepper();
  const supportHref = useFeatureValue(FEATURE_FLAGS.SUPPORT_LINK, '');
  const { logoutMutation } = useAuthMutations();

  const openVerificationURL = async () => {
    if (idVerificationSession?.url) {
      const platform = Capacitor.getPlatform();

      if (platform === 'web') {
        return window.open(idVerificationSession?.url, '_self');
      }

      await Browser.open({ url: idVerificationSession?.url });
    }
  };

  const handleLogout = async () => {
    logoutMutation.mutate();
  };

  const handleBack = () => {
    if (isPending) {
      setIsPending(false);
      refetch();
      return;
    }

    if (enableLogout) {
      return handleLogout();
    }

    stepperContext.moveBackward();
  };

  const handleSupportClick = () => {
    window.open(supportHref, '_blank', 'noopener,noreferrer');
  };

  const handleVerificationStart = () => {
    openVerificationURL();

    setTimeout(() => setIsPending(true), 2000);
  };

  const enableLogout = stepperContext.isFirstStep && !isPending;
  const backButtonLabel = isPending
    ? 'Intentar de nuevo'
    : !enableLogout
      ? 'Regresar'
      : 'Cerrar sesión';

  const handleFinishedVerification = () => {
    onFinishedVerification?.();
  };

  return (
    <AnimatedStepWrapper>
      <Typography
        variant="h5"
        textAlign="center"
        fontWeight="fontWeightMedium"
        gutterBottom
      >
        {isPending ? 'Completa tu verificación' : 'Antes de continuar'}
      </Typography>
      <Typography
        textAlign="left"
        variant="body1"
        fontWeight="fontWeightRegular"
      >
        {isPending
          ? 'Si ya terminaste de verificar tu identidad, da clic en el botón de abajo'
          : 'Necesitamos validar que eres mayor de edad. Ten tu documento de identidad a la mano'}
      </Typography>
      <Button
        type="submit"
        variant="contained"
        color="primary"
        size="large"
        disabled={isLoading}
        onClick={
          isPending ? handleFinishedVerification : handleVerificationStart
        }
        sx={{ marginTop: 'auto' }}
      >
        {isPending ? 'Ya terminé el proceso' : 'Continuar'}
      </Button>
      <AuthStepperFooter
        backButton={{
          color: 'greyText',
          label: backButtonLabel,
          onClick: handleBack,
          variant: 'text',
        }}
        nextButton={{
          color: 'error',
          label: 'Ayuda',
          onClick: handleSupportClick,
          variant: 'text',
        }}
      />
    </AnimatedStepWrapper>
  );
}
